import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { ApiService } from '../../../shared/services/api.service';
import {
  ExecutionSource,
  GetQueryHistoryForChartQuery,
  GetQueryHistoryForChartQueryVariables,
  QueryHistoryChartRecord,
  QueryHistoryInput,
} from '../../../../API';
import { GetQueryHistoryForChart } from '../../../../graphql/queries';
import { NzMessageService } from 'ng-zorro-antd/message';
import { ChartDataService } from '../../services/chart-data.service';
import { QueryHistoryService } from '../../../market-fan-exploration/services/query-history.service';
import { WorkbenchResultQueryRecord } from '../../../market-fan-exploration/models/history-model';

@Component({
  selector: 'app-create-chart',
  templateUrl: './create-chart.component.html',
  styleUrl: './create-chart.component.scss',
})
export class CreateChartComponent implements OnInit {
  selectedFilters: string[] = [];
  currentPage = 1;
  filterOptions: { label: string; value: string }[] = [];
  allQueryResults: QueryHistoryChartRecord[] = [];
  loadingQueries = true;
  sortDescending = true;
  totalQueries = 0;
  pageSize = 10;
  queryResults: QueryHistoryChartRecord[] = [];
  queryDisplayNames = new Map<string, string>();
  selectedQuery: QueryHistoryChartRecord | null = null;
  nextBtnTooltip = '';
  @Output() goToNextStep = new EventEmitter<void>();

  get workbenchSelectedQuery(): WorkbenchResultQueryRecord | null {
    const orgId = this.selectedQuery?.orgId ?? '';
    const queryId = this.selectedQuery?.queryId ?? '';
    const executionId = this.selectedQuery?.executionId ?? '';
    const athenaExecutionId = this.selectedQuery?.athenaExecutionId ?? '';
    const metadata = this.selectedQuery?.metadata ?? '{}';

    if (!orgId || !queryId || !executionId || !athenaExecutionId) {
      return null;
    }

    return {
      orgId,
      queryId,
      executionId,
      athenaExecutionId,
      metadata,
    };
  }

  constructor(
    private userManagementService: UserManagementService,
    private apiService: ApiService,
    private nzMessageService: NzMessageService,
    private chartDataService: ChartDataService,
    private queryHistoryService: QueryHistoryService,
  ) {}

  ngOnInit(): void {
    this.getQueryResults();
  }

  getQueryResults(): void {
    this.loadingQueries = true;
    this.queryDisplayNames.clear();
    const orgId = this.userManagementService.getOrgId();

    const inputWorkbench: QueryHistoryInput = {
      pkName: 'orgId',
      pkValue: orgId,
      skName: 'executionSource',
      skValue: ExecutionSource.WORKBENCH,
    };
    const inputScheduler: QueryHistoryInput = {
      pkName: 'orgId',
      pkValue: orgId,
      skName: 'executionSource',
      skValue: ExecutionSource.SCHEDULER,
    };

    Promise.all([
      this.apiService.query<GetQueryHistoryForChartQuery, GetQueryHistoryForChartQueryVariables>(
        GetQueryHistoryForChart,
        { input: inputWorkbench },
      ),
      this.apiService.query<GetQueryHistoryForChartQuery, GetQueryHistoryForChartQueryVariables>(
        GetQueryHistoryForChart,
        { input: inputScheduler },
      ),
    ])
      .then(([responseWorkbench, responseScheduler]) => {
        const resultWorkbench = responseWorkbench?.data?.GetQueryHistoryForChart || [];
        const resultScheduler = responseScheduler?.data?.GetQueryHistoryForChart || [];
        this.allQueryResults = [...resultWorkbench, ...resultScheduler];
        this.setQueryDisplayNames();
        this.setFilterOptions();
        this.updatePaginatedResults();
        this.autoSelectFirstQuery();
      })
      .catch((error) => {
        this.nzMessageService.error(`${error} Failed to get query results`);
      })
      .finally(() => {
        this.loadingQueries = false;
      });
  }

  setQueryDisplayNames(): void {
    const recordsByName = new Map<string, QueryHistoryChartRecord[]>();

    this.allQueryResults.forEach((record) => {
      const name = record.name || '';
      const records = recordsByName.get(name) || [];
      records.push(record);
      recordsByName.set(name, records);
    });

    recordsByName.forEach((records) => {
      if (records.length < 2) {
        return;
      }

      records
        .sort(
          (first, second) =>
            new Date(first.lastModifiedAt || 0).getTime() - new Date(second.lastModifiedAt || 0).getTime(),
        )
        .forEach((record, index) => {
          this.queryDisplayNames.set(record.executionId, `${record.name || ''} - ${index + 1}`);
        });
    });
  }

  getQueryDisplayName(query: QueryHistoryChartRecord): string {
    return this.queryDisplayNames.get(query.executionId) || query.name || '';
  }

  setFilterOptions(): void {
    this.filterOptions = this.allQueryResults
      .map((record: QueryHistoryChartRecord) => record.createdBy)
      .filter((createdBy, index, arr) => createdBy && arr.indexOf(createdBy) === index)
      .map((createdBy) => ({
        label: createdBy || 'Unknown',
        value: createdBy || 'Unknown',
      }));
  }

  toggleSortDirection(): void {
    this.sortDescending = !this.sortDescending;
    this.currentPage = 1;
    this.updatePaginatedResults();
    this.autoSelectFirstQuery();
  }

  updatePaginatedResults(): void {
    const filteredResults = this.getFilteredAndSortedResults();
    this.totalQueries = filteredResults.length;
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.queryResults = filteredResults.slice(startIndex, endIndex);
  }

  autoSelectFirstQuery(): void {
    if (this.queryResults.length > 0) {
      this.selectQuery(this.queryResults[0]);
    } else if (this.allQueryResults.length > 0) {
      this.selectQuery(this.allQueryResults[0]);
    } else {
      this.selectedQuery = null;
    }
  }

  getFilteredAndSortedResults(): QueryHistoryChartRecord[] {
    let results = [...this.allQueryResults];

    if (this.selectedFilters.length > 0) {
      results = results.filter((query) => this.selectedFilters.includes(query.createdBy || 'Unknown'));
    }

    if (this.sortDescending) {
      results.sort((a, b) => new Date(b.lastModifiedAt || 0).getTime() - new Date(a.lastModifiedAt || 0).getTime());
    } else {
      results.sort((a, b) => new Date(a.lastModifiedAt || 0).getTime() - new Date(b.lastModifiedAt || 0).getTime());
    }

    return results;
  }

  selectQuery(query: QueryHistoryChartRecord): void {
    this.selectedQuery = query;
    const metadata = this.selectedQuery.metadata || '{}';
    this.chartDataService.setType(metadata);
    this.chartDataService.queryRecord = this.selectedQuery;
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.updatePaginatedResults();
    this.autoSelectFirstQuery();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.updatePaginatedResults();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.updatePaginatedResults();
  }

  getRowCount(query: QueryHistoryChartRecord): string {
    if (query.metadata) {
      try {
        const metadata = JSON.parse(query.metadata);
        return metadata.totalRecords ? metadata.totalRecords : '--';
      } catch {
        return '--';
      }
    }
    return '--';
  }

  get isQueryValid(): boolean {
    const rowCount = (this.selectedQuery && this.getRowCount(this.selectedQuery)) || 0;
    let isTypeAvailable = false;
    try {
      isTypeAvailable = this.selectedQuery?.metadata
        ? JSON.parse(this.selectedQuery?.metadata || '{}')?.columnsList?.every((col: any) => col.type)
        : false;
    } catch (e) {
      console.error('Error parsing metadata:', e);
    }
    if (!isTypeAvailable) {
      this.nextBtnTooltip = 'Data type for one or more column is not available from query result';
    } else if (rowCount == '0') {
      this.nextBtnTooltip = 'No Data available';
    } else if (rowCount == '--') {
      this.nextBtnTooltip = 'Invalid row count';
    } else {
      this.nextBtnTooltip = '';
    }
    return rowCount !== '--' && rowCount !== '0' && isTypeAvailable;
  }
  onGoToNextStep() {
    this.goToNextStep.emit();
  }
}
